import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';

const { FiSave, FiArrowLeft, FiClock, FiLayers, FiTarget, FiCheckCircle, FiPlayCircle } = FiIcons;

function EntryForm({ onSubmit, entries = [], isEdit = false }) {
  const navigate = useNavigate();
  const { id } = useParams();

  // State to track if we are in "Result Logging" mode
  const [showResultsForm, setShowResultsForm] = useState(false);
  const [formData, setFormData] = useState({
    taskName: '',
    // Baseline (Initial)
    timeBefore: '',
    stepsBefore: '',
    confusionBefore: 5,
    // Results (After) - Optional initially
    timeAfter: '',
    stepsAfter: '',
    confusionAfter: 5,
    notes: '',
    status: 'in-progress' // 'in-progress' | 'has-results'
  });
  const [errors, setErrors] = useState({});
  const [logLimitReached, setLogLimitReached] = useState(false);

  useEffect(() => {
    if (isEdit && id && entries.length > 0) {
      const entry = entries.find(e => e.id === parseInt(id));
      if (entry) {
        setFormData({
          taskName: entry.taskName,
          timeBefore: entry.timeBefore,
          stepsBefore: entry.stepsBefore,
          confusionBefore: entry.confusionBefore,
          timeAfter: entry.timeAfter !== null ? entry.timeAfter : '',
          stepsAfter: entry.stepsAfter !== null ? entry.stepsAfter : '',
          confusionAfter: entry.confusionAfter !== null ? entry.confusionAfter : 5,
          notes: entry.notes || '',
          status: entry.status || (entry.timeAfter !== null ? 'has-results' : 'in-progress')
        });
        
        // Check if we've reached the log limit
        if (entry.log_count >= 5) {
          setLogLimitReached(true);
        }
        
        // If editing an entry that already has results, show the result form
        if (entry.timeAfter !== null && entry.timeAfter !== undefined && entry.timeAfter !== '') {
          setShowResultsForm(true);
        }
      }
    }
  }, [isEdit, id, entries]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.taskName.trim()) {
      newErrors.taskName = 'Task name is required';
    }

    // Validate Baseline
    if (!formData.timeBefore || formData.timeBefore < 0) {
      newErrors.timeBefore = 'Initial time is required';
    }
    if (!formData.stepsBefore || formData.stepsBefore < 0) {
      newErrors.stepsBefore = 'Initial steps are required';
    }

    // Validate Results ONLY if we are logging results
    if (showResultsForm && !logLimitReached) {
      if (formData.timeAfter === '' || formData.timeAfter < 0) {
        newErrors.timeAfter = 'Final time is required';
      }
      if (formData.stepsAfter === '' || formData.stepsAfter < 0) {
        newErrors.stepsAfter = 'Final steps are required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if we're trying to log results but have reached the limit
    if (showResultsForm && logLimitReached) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    const hasResults = showResultsForm && formData.timeAfter !== '' && !logLimitReached;
    
    const submissionData = {
      ...formData,
      // Parse numbers
      timeBefore: parseInt(formData.timeBefore),
      stepsBefore: parseInt(formData.stepsBefore),
      confusionBefore: parseInt(formData.confusionBefore),
      // Handle optional results
      timeAfter: hasResults ? parseInt(formData.timeAfter) : null,
      stepsAfter: hasResults ? parseInt(formData.stepsAfter) : null,
      confusionAfter: hasResults ? parseInt(formData.confusionAfter) : null,
      status: hasResults ? 'has-results' : 'in-progress'
    };

    if (isEdit) {
      onSubmit(parseInt(id), submissionData);
    } else {
      onSubmit(submissionData);
    }
    navigate('/');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Calculations for preview
  const hasResults = formData.timeAfter !== '' && formData.timeBefore !== '';
  const timeSaved = hasResults ? parseInt(formData.timeBefore) - parseInt(formData.timeAfter) : 0;
  const stepsReduced = (formData.stepsBefore && formData.stepsAfter !== '') ? parseInt(formData.stepsBefore) - parseInt(formData.stepsAfter) : 0;
  const confusionReduction = (formData.confusionBefore && formData.confusionAfter) ? parseInt(formData.confusionBefore) - parseInt(formData.confusionAfter) : 0;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="max-w-4xl mx-auto"
    >
      <div className="mb-8">
        <button 
          onClick={() => navigate('/')} 
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
        >
          <SafeIcon icon={FiArrowLeft} className="text-lg" />
          <span>Back to Dashboard</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdit ? 'Manage Sprint' : 'Define New Sprint'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEdit ? 'Update your task details or log your progress results.' : 'Set the baseline for your new foundational task.'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* SECTION 1: TASK DEFINITION & BASELINE */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
              <div className="flex items-center space-x-2 border-b border-gray-100 pb-4 mb-4">
                <SafeIcon icon={FiPlayCircle} className="text-purple-600 text-xl" />
                <h2 className="text-xl font-semibold text-gray-900">1. Define Baseline</h2>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task/System Name *
                </label>
                <input 
                  type="text" 
                  name="taskName" 
                  value={formData.taskName} 
                  onChange={handleChange} 
                  placeholder="e.g., Customer onboarding process"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.taskName ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.taskName && (
                  <p className="text-red-600 text-sm mt-1">{errors.taskName}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Time (min) *
                  </label>
                  <input 
                    type="number" 
                    name="timeBefore" 
                    value={formData.timeBefore} 
                    onChange={handleChange} 
                    min="0" 
                    placeholder="Current duration"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      errors.timeBefore ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Steps *
                  </label>
                  <input 
                    type="number" 
                    name="stepsBefore" 
                    value={formData.stepsBefore} 
                    onChange={handleChange} 
                    min="0" 
                    placeholder="Current step count"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      errors.stepsBefore ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Initial Confusion/Friction: <span className="font-bold text-purple-600">{formData.confusionBefore}/10</span>
                </label>
                <input 
                  type="range" 
                  name="confusionBefore" 
                  value={formData.confusionBefore} 
                  onChange={handleChange} 
                  min="1" 
                  max="10"
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 (Clear)</span>
                  <span>10 (Chaos)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Baseline Notes
                </label>
                <textarea 
                  name="notes" 
                  value={formData.notes} 
                  onChange={handleChange} 
                  rows="3" 
                  placeholder="Describe the current problem state..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                />
              </div>
            </div>

            {/* SECTION 2: RESULTS TOGGLE */}
            {!showResultsForm && !logLimitReached && (
              <div className="flex justify-center">
                <button 
                  type="button" 
                  onClick={() => setShowResultsForm(true)} 
                  className="text-purple-600 font-medium hover:text-purple-800 flex items-center space-x-2 bg-purple-50 px-6 py-3 rounded-lg border border-purple-100 transition-colors"
                >
                  <SafeIcon icon={FiCheckCircle} />
                  <span>I have results to log now</span>
                </button>
              </div>
            )}

            {/* Log Limit Warning */}
            {logLimitReached && (
              <div className="bg-red-50 border-l-4 border-red-400 p-6">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="text-red-600 text-xl" />
                  <h3 className="text-lg font-semibold text-red-900">Log Limit Reached</h3>
                </div>
                <p className="text-red-800 mt-2">
                  You've reached the maximum of 5 logs for this item. No more results can be logged.
                </p>
              </div>
            )}

            {/* SECTION 3: RESULTS FORM */}
            {showResultsForm && !logLimitReached && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} 
                animate={{ opacity: 1, height: 'auto' }} 
                className="bg-blue-50 rounded-xl shadow-sm border border-blue-200 p-6 space-y-6"
              >
                <div className="flex items-center justify-between border-b border-blue-200 pb-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="text-blue-600 text-xl" />
                    <h2 className="text-xl font-semibold text-blue-900">2. Log Results</h2>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowResultsForm(false);
                      setFormData(prev => ({ 
                        ...prev, 
                        timeAfter: '', 
                        stepsAfter: '', 
                        confusionAfter: 5 
                      }));
                    }} 
                    className="text-sm text-blue-700 hover:text-blue-900 underline"
                  >
                    Cancel Results
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Final Time (min) *
                    </label>
                    <input 
                      type="number" 
                      name="timeAfter" 
                      value={formData.timeAfter} 
                      onChange={handleChange} 
                      min="0" 
                      placeholder="New duration"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white ${
                        errors.timeAfter ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.timeAfter && <p className="text-red-600 text-sm mt-1">{errors.timeAfter}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Final Steps *
                    </label>
                    <input 
                      type="number" 
                      name="stepsAfter" 
                      value={formData.stepsAfter} 
                      onChange={handleChange} 
                      min="0" 
                      placeholder="New step count"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white ${
                        errors.stepsAfter ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.stepsAfter && <p className="text-red-600 text-sm mt-1">{errors.stepsAfter}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    Final Confusion/Friction: <span className="font-bold text-blue-700">{formData.confusionAfter}/10</span>
                  </label>
                  <input 
                    type="range" 
                    name="confusionAfter" 
                    value={formData.confusionAfter} 
                    onChange={handleChange} 
                    min="1" 
                    max="10"
                    className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-xs text-blue-700 mt-1">
                    <span>1 (Crystal Clear)</span>
                    <span>10 (Still Confusing)</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="flex items-center justify-end space-x-4 pt-6">
              <button 
                type="button" 
                onClick={() => navigate('/')} 
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={logLimitReached && showResultsForm}
                className={`flex items-center space-x-2 px-6 py-2 text-white rounded-lg transition-colors font-medium shadow-sm ${
                  logLimitReached && showResultsForm
                    ? 'bg-gray-400 cursor-not-allowed'
                    : showResultsForm 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                <SafeIcon icon={FiSave} className="text-lg" />
                <span>
                  {logLimitReached && showResultsForm 
                    ? 'Log Limit Reached' 
                    : showResultsForm 
                      ? 'Log Results' 
                      : 'Save Baseline'
                  }
                </span>
              </button>
            </div>
          </form>
        </div>

        {/* SIDEBAR: IMPACT PREVIEW */}
        <div className="space-y-6">
          <div className={`rounded-xl shadow-sm border p-6 transition-colors ${
            showResultsForm ? 'bg-white border-blue-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {showResultsForm ? 'Projected Impact' : 'Potential Impact'}
            </h3>
            {!showResultsForm ? (
              <p className="text-sm text-gray-500 italic">
                Log your results to see the calculated improvements here.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Time Saved:</span>
                  <span className={`font-semibold ${
                    timeSaved > 0 ? 'text-green-600' : timeSaved < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {timeSaved > 0 ? '+' : ''}{timeSaved} min
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Steps Reduced:</span>
                  <span className={`font-semibold ${
                    stepsReduced > 0 ? 'text-green-600' : stepsReduced < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stepsReduced > 0 ? '+' : ''}{stepsReduced}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Confusion Reduced:</span>
                  <span className={`font-semibold ${
                    confusionReduction > 0 ? 'text-green-600' : confusionReduction < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {confusionReduction > 0 ? '+' : ''}{confusionReduction}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="bg-purple-50 rounded-xl border border-purple-200 p-6">
            <h3 className="text-lg font-semibold text-purple-900 mb-3">Sprint Guide</h3>
            <ul className="text-sm text-purple-800 space-y-3">
              <li className="flex items-start space-x-2">
                <span className="font-bold">•</span>
                <span><strong>Phase 1:</strong> Define the baseline honestly. How bad is it right now?</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-bold">•</span>
                <span><strong>Phase 2:</strong> Do the work! Simplify, automate, or document.</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-bold">•</span>
                <span><strong>Phase 3:</strong> Come back and log the new numbers to see your win.</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-bold">•</span>
                <span><strong>Max 5 logs:</strong> Each item can be logged up to 5 times.</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-bold">•</span>
                <span><strong>Completion:</strong> Mark items complete with the checkbox.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default EntryForm;